import { NegotiationRoundStatus, type NegotiationRoundV2Dto, type SourcingEventV2Dto } from "../../../../autogen/bff-api";

export interface StageDate {
  iso: string;
  planned: boolean;
}

/**
 * A bidding stage: the original competition, then one per negotiation round. Each closes at a
 * deadline and is followed by an evaluation — the buyer reviews what came in before deciding
 * whether to negotiate again.
 */
export interface NegotiationStage {
  id: string;
  // null for the original competition; the round number otherwise.
  roundNumber: number | null;
  deadline: string;
  evaluation: StageDate | null;
}

const settled = (iso: string | null | undefined): StageDate | null =>
  iso ? { iso, planned: false } : null;

/**
 * How long after a deadline the plan plans to evaluate, taken from the server's own two numbers
 * rather than re-stating the BFF's default offset here. Null when either is missing.
 */
const evaluationOffsetMs = (event: SourcingEventV2Dto): number | null => {
  const dates = event.milestoneDates;
  const anchor = dates?.plannedTenderDeadline ?? dates?.bidSubmissionDeadline;
  if (!anchor || !dates?.evaluationTarget) return null;
  return Date.parse(dates.evaluationTarget) - Date.parse(anchor);
};

const plannedEvaluation = (deadline: string, offsetMs: number | null): StageDate | null =>
  offsetMs == null
    ? null
    : { iso: new Date(Date.parse(deadline) + offsetMs).toISOString(), planned: true };

const completedAtOf = (round: NegotiationRoundV2Dto): string | null =>
  round.status === NegotiationRoundStatus.Completed ? round.completedAt ?? null : null;

/**
 * `rounds` must already be filtered to the rounds that have been sent. The original competition is
 * only a stage of its own once a round exists — before that the track speaks for it directly.
 */
export const negotiationStages = (
  event: SourcingEventV2Dto,
  rounds: readonly NegotiationRoundV2Dto[],
): NegotiationStage[] => {
  const offsetMs = evaluationOffsetMs(event);
  const originalDeadline = event.milestoneDates?.bidSubmissionDeadline ?? null;

  const original: NegotiationStage[] =
    originalDeadline == null
      ? []
      : [
          {
            id: "original",
            roundNumber: null,
            deadline: originalDeadline,
            // With rounds in play the event's evaluation belongs to the last of them, not to the
            // original offers, so the original stage keeps its planned date.
            evaluation:
              rounds.length === 0
                ? settled(event.evaluationStartedAt) ?? plannedEvaluation(originalDeadline, offsetMs)
                : plannedEvaluation(originalDeadline, offsetMs),
          },
        ];

  return [
    ...original,
    ...rounds.map((round, index) => ({
      id: round.id,
      roundNumber: round.roundNumber,
      deadline: round.deadline,
      evaluation:
        settled(completedAtOf(round)) ??
        (index === rounds.length - 1 ? settled(event.evaluationStartedAt) : null) ??
        plannedEvaluation(round.deadline, offsetMs),
    })),
  ];
};

export const latestStage = (stages: readonly NegotiationStage[]): NegotiationStage | null =>
  stages.length > 0 ? stages[stages.length - 1] : null;
